import React, { useCallback, useEffect, useState } from 'react' //useRef
import { mergeLeft } from 'ramda'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import SearchIcon from '@material-ui/icons/Search'
import { Form, useFormik, FormikProvider } from 'formik'
import { FormControl, InputAdornment, makeStyles } from '@material-ui/core'

import DataTable from 'app/componentV4/Table/DataTable'
import SimpleInput from 'app/componentV4/Input/SimpleInput'

import { usePlace } from 'commons/usePlace'
import { STATUS_INVITED } from 'commons/useTransfer'
import { AddSubscribers } from 'app/components/icons'
import { useIsRootAdmin } from 'commons/useUserProfile'
import TransferPanel from 'app/components/TransferPanel'
import { ReactComponent as EmptyTeamMemberIcon } from 'app/components/icons/empty-team-member-icon.svg'

import { SECONDARY } from 'app/constants/button'

import EmptyState from 'app/componentV4/EmptyState'
import PanelPages from 'app/componentV4/Panels/Pages'
import Delete from 'app/main/settings/teamMembersV4/Action/Delete'
import OutlinedButtons from 'app/componentV4/Button/OutlinedButtons'
import ExportPDF from 'app/main/settings/teamMembersV4/Action/ExportPDF'
import ExportCSV from 'app/main/settings/teamMembersV4/Action/ExportCSV'
import useTeamMember from 'app/main/settings/teamMembersV4/useTeamMember'
import { ColumnOfData } from 'app/main/settings/teamMembersV4/ColumnOfData'
import TeamMemberDialog from 'app/main/settings/teamMembersV4/TeamMemberDialog'
import {
  INACTIVE,
  DELETE,
  SUSPEND,
  RE_STORE,
  RE_INVITE,
} from 'app/main/settings/teamMembersV4/constants'

const useStyles = makeStyles((theme) => ({
  table: {
    '&.data-table': {
      [theme.breakpoints.up('md')]: {
        height: 'calc(100vh - 155px)',
      },
    },
  },
}))

const TeamMembers = () => {
  const place = usePlace()
  const classes = useStyles()
  const { formatMessage } = useIntl()
  const isRootAdmin = useIsRootAdmin()
  const { selectedPlace } = useSelector(({ auth }) => auth.user)
  const [isOpen, setIsOpen] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [selectedIds, setSelectedIds] = useState({})
  const [selectedRow, setSelectedRow] = useState(null) //store row{}
  const [isResetSelected, setIsResetSelected] = useState(false)

  const {
    state: {
      params,
      teamMembers,
      isSubmitting,
      isEmptyTeamMembers,
      isLoadingTeamMembers,
    },
    handleSearch,
    updateResend,
    updateActive,
    updateSuspend,
    handleExportPDF,
    handleExportCSV,
    createTeamMembers,
    deleteTeamMember,
    updateTeamMembers,
    handleFetchTeamMembers,
  } = useTeamMember(
    () => setIsOpen(false),
    () => setIsResetSelected(true),
  )

  const { data, meta } = teamMembers
  const rootAdmin = data.find(({ id }) => id === place.creatorId) ?? {}

  const formik = useFormik({
    initialValues: {
      search: '',
    },
  })

  const handleSelectedRow = (data) => {
    setIsOpen(true)
    setIsEdit(true)
    setSelectedRow(data)
  }

  const handleClose = () => {
    setIsOpen(false)
    setIsEdit(false)
  }

  const handleSubmit = async (data) => {
    if (isEdit) await updateTeamMembers({ ...data, id: selectedRow.id })
    else await createTeamMembers(data)
  }

  const actionCreateTeamMember = () => {
    return (
      <OutlinedButtons
        id="add-teamMembers-btn"
        color={SECONDARY}
        onClick={() => setIsOpen(true)}
        className="w-full"
      >
        <div className="flex">
          <AddSubscribers classes="w-22 mr-3" />
          {formatMessage({
            id: 'teamMembers.lable.add.teamMembers',
          })}
        </div>
      </OutlinedButtons>
    )
  }

  const handleIndividualDialogAction = (type, id) => {
    switch (type) {
      case RE_STORE:
        return updateActive([id])
      case RE_INVITE:
        return updateResend([id])
      case SUSPEND:
        return updateSuspend([id])
      case DELETE:
        return deleteTeamMember([id])
      default:
        return
    }
  }

  const fetchTeamMemberList = useCallback(() => {
    handleFetchTeamMembers(params)
  }, [handleFetchTeamMembers, params])

  useEffect(fetchTeamMemberList, [])

  useEffect(() => {
    if (!isOpen) {
      setSelectedRow({})
      setIsEdit(false)
    }
  }, [isOpen])

  useEffect(() => {
    if (!isLoadingTeamMembers) setIsResetSelected(false)
  }, [isLoadingTeamMembers])

  return (
    <>
      <div>
        {selectedPlace.transferRequestFrom &&
          selectedPlace.transferRequestFrom.status === STATUS_INVITED && (
            <TransferPanel
              firstName={selectedPlace.transferRequestFrom.user.firstName}
              lastName={selectedPlace.transferRequestFrom.user.lastName}
              transferRequestId={selectedPlace.transferRequestFrom.id}
              type={2}
            />
          )}
      </div>
      <PanelPages
        isEmptyState={isEmptyTeamMembers}
        emptyState={
          <EmptyState
            icon={<EmptyTeamMemberIcon />}
            title={formatMessage({
              id: 'teamMembers.empty.state.title',
            })}
            description={formatMessage({
              id: 'teamMembers.empty.state.description',
            })}
            accesses={[]}
            onAction={<div className="w-fit">{actionCreateTeamMember()}</div>}
          />
        }
        title={formatMessage({
          id: 'teamMembers.header.label',
        })}
        rightContent={
          <FormikProvider value={formik}>
            <Form>
              <div className="space-y-5 md:space-y-0 md:space-x-10 md:flex items-center">
                <FormControl variant="outlined" className="w-full md:w-256">
                  <SimpleInput
                    id="search-timeline-by-title"
                    name="search"
                    placeholder={formatMessage(
                      {
                        id: 'searchBar.placeholder.text',
                      },
                      {
                        subject: formatMessage({
                          id: 'teamMembers.header.label',
                        }),
                      },
                    )}
                    value={formik.values.search}
                    fullWidth
                    size="small"
                    onChange={(event) => {
                      formik.setFieldValue('search', event.target.value)
                      handleSearch(event.target.value)
                    }}
                    startAdornment={
                      <InputAdornment position="start">
                        <SearchIcon className="text-grey-400" />
                      </InputAdornment>
                    }
                  />
                </FormControl>
                <div className="w-full md:w-fit">
                  {actionCreateTeamMember()}
                </div>
              </div>
            </Form>
          </FormikProvider>
        }
      >
        <DataTable
          data={data}
          isCheckbox={true}
          className={classes.table}
          columns={ColumnOfData(
            formatMessage,
            handleIndividualDialogAction,
            isRootAdmin,
            (value) => setSelectedRow(value), // return row or null
          )}
          pagination={meta}
          isLoading={isLoadingTeamMembers}
          isResetSelected={isResetSelected}
          onFetch={(value) => handleFetchTeamMembers(mergeLeft(value, params))}
          onSelectedRow={(data) => handleSelectedRow(data)}
          activeRow={selectedRow?.id}
          onChange={(value) => setSelectedIds(value)}
          bulkAction={
            <div className="divide-x divide-neutrals-400 flex">
              <div className="px-5">
                <ExportCSV
                  selectedCount={selectedIds[meta.currentPage]?.length ?? 0}
                  onAction={() =>
                    handleExportCSV(data, selectedIds[meta.currentPage])
                  }
                />
              </div>
              <div className="px-5">
                <ExportPDF
                  selectedCount={selectedIds[meta.currentPage]?.length ?? 0}
                  onAction={() =>
                    handleExportPDF(data, selectedIds[meta.currentPage])
                  }
                />
              </div>
              <div className="pl-5">
                <Delete
                  selectedCount={selectedIds[meta.currentPage]?.length ?? 0}
                  rootAdmin={rootAdmin}
                  selected={selectedIds[meta.currentPage] ?? []}
                  onAction={(data) => deleteTeamMember(data)}
                />
              </div>
            </div>
          }
        />
      </PanelPages>
      <TeamMemberDialog
        open={isOpen}
        onClose={() => handleClose()}
        isSubmitting={isSubmitting}
        onSubmitted={(values) => handleSubmit(values)}
        initialData={selectedRow}
      />
    </>
  )
}

export default TeamMembers
